import { Link } from "react-router-dom";
import { ArrowRight, Mail, Phone, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { HeroSlider } from "@/components/landing/HeroSlider";
import { SocialLinks } from "@/components/Social";
import { InstallApp } from "@/components/InstallApp";

export const Section = ({ id, kicker, title, dark, children }) => (
  <section id={id} className={dark ? "bg-[#0B192C] text-white" : ""} data-testid={`landing-section-${id}`}>
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-20 sm:py-24">
      <div className={`text-xs font-mono-x uppercase tracking-[0.25em] mb-3 ${dark ? "text-emerald-400" : "text-emerald-700"}`}>{kicker}</div>
      <h2 className={`font-heading text-4xl sm:text-5xl font-bold uppercase tracking-tight mb-10 ${dark ? "text-white" : "text-[#0B192C]"}`}>{title}</h2>
      {children}
    </div>
  </section>
);

const NAV = [["chi-siamo", "Chi siamo"], ["news", "News"], ["classi", "Le classi"], ["regate", "Regate"], ["ranking", "Ranking"]];

export const LandingNav = ({ nome, logged, hasRanking }) => (
  <header className="absolute top-0 inset-x-0 z-20">
    <div className="max-w-6xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
      <a href="#top" className="flex items-center gap-3" data-testid="landing-logo">
        <img src="/logo-9er.png" alt="Logo" className="h-11 w-11 rounded-lg bg-white p-1" />
        <span className="font-heading uppercase text-white text-sm sm:text-base leading-tight max-w-[180px]">{nome}</span>
      </a>
      <nav className="hidden md:flex gap-7 text-sm font-heading uppercase tracking-wider text-white/80">
        {NAV.filter(([id]) => id !== "ranking" || hasRanking).map(([id, l]) => <a key={id} href={`#${id}`} className="hover:text-white transition-colors" data-testid={`landing-nav-${id}`}>{l}</a>)}
      </nav>
      <div className="flex items-center gap-2">
      <InstallApp className="text-white h-10" />
      <Link to={logged ? "/" : "/login"} data-testid="landing-login-link">
        <Button variant="outline" className="rounded-full bg-white/10 border-white/30 text-white hover:bg-white hover:text-[#0B192C]">{logged ? "Vai all'app" : "Accedi"}</Button>
      </Link>
      </div>
    </div>
  </header>
);

export const Hero = ({ nome, logged, classi }) => (
  <section id="top" className="relative min-h-[88vh] flex items-end overflow-hidden" data-testid="landing-hero">
    <HeroSlider classi={classi} />
    <div className="relative max-w-6xl mx-auto w-full px-5 sm:px-8 pb-20 pt-32">
      <div className="tricolore h-1.5 w-24 mb-6" />
      <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold uppercase text-white leading-[0.95] max-w-3xl fade-up">{nome}</h1>
      <p className="text-white/80 text-base sm:text-lg mt-5 max-w-xl fade-up">29er · 49er · 49er FX · Nacra 15 · Nacra 17: la casa italiana degli skiff e dei catamarani olimpici e giovanili.</p>
      <div className="flex flex-wrap gap-3 mt-8">
        <IscrivitiCta logged={logged} />
        <a href="#classi"><Button variant="ghost" className="rounded-full text-white hover:bg-white/10 h-12 px-6" data-testid="hero-classi-button">Scopri le classi</Button></a>
      </div>
    </div>
  </section>
);

export const IscrivitiCta = ({ logged, testid = "hero-iscriviti-button" }) => (
  <Link to={logged ? "/tessera" : "/registrazione"} data-testid={testid}>
    <Button className="rounded-full h-12 px-7 bg-emerald-600 hover:bg-emerald-500 text-white font-heading uppercase tracking-wider text-base group">
      Iscriviti ora <ArrowRight className="h-4 w-4 ml-2 transition-transform group-hover:translate-x-1" />
    </Button>
  </Link>
);

export const Contatti = ({ d }) => {
  const sede = [d.indirizzo, [d.cap, d.citta].filter(Boolean).join(" ")].filter(Boolean).join(", ");
  const rows = [[MapPin, sede], [Mail, d.email, `mailto:${d.email}`], [Phone, d.telefono, `tel:${d.telefono}`]].filter((r) => r[1]);
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-7" data-testid="landing-contatti">
      <h3 className="font-heading text-2xl uppercase text-[#0B192C] mb-5">Contatti</h3>
      <ul className="space-y-4 text-slate-700">
        {rows.map(([Icon, text, href]) => (
          <li key={text} className="flex gap-3 items-start">
            <Icon className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
            {href ? <a href={href} className="hover:underline break-all">{text}</a> : <span>{text}</span>}
          </li>
        ))}
        {rows.length === 0 && <li className="text-muted-foreground">Contatti non ancora disponibili.</li>}
      </ul>
      <div className="mt-6"><SocialLinks d={d} /></div>
    </div>
  );
};

export const LandingFooter = ({ d }) => (
  <footer className="bg-[#07111F] text-white/70 text-sm" data-testid="landing-footer">
    <div className="tricolore h-1" />
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10 flex flex-col sm:flex-row gap-4 justify-between">
      <div>
        <div className="font-heading uppercase text-white text-base">{d.nome}</div>
        <div>{[d.indirizzo, d.cap, d.citta].filter(Boolean).join(" · ")}{d.codice_fiscale && ` · C.F. ${d.codice_fiscale}`}</div>
      </div>
      <div className="flex gap-5 items-center">
        <SocialLinks d={d} dark />
        <Link to="/privacy" className="hover:text-white underline-offset-4 hover:underline" data-testid="landing-privacy-link">Privacy policy</Link>
        <Link to="/login" className="hover:text-white" data-testid="landing-footer-login-link">Area soci</Link>
      </div>
    </div>
  </footer>
);
